import React from "react";
// Shared consultation data + state hook used by all 3 variations.

// Six service categories — used as starter chips, recommendation cards,
// and the "What can we help with" surface.
export const NX_SERVICES = [
  {
    id: "ai",
    title: "AI Business Solutions",
    short: "AI for your business",
    body: "AI implementation, chatbots, customer support, knowledge search, sales assistants, and AI-powered workflows.",
    examples: "AI chatbot · AI customer support · AI knowledge search · AI sales assistant",
    icon: "ai",
    cta: "Talk about AI",
  },
  {
    id: "product",
    title: "Product / MVP Development",
    short: "Build a product from scratch",
    body: "End-to-end partnership: discovery, planning, UI/UX, MVP build, launch, and iteration.",
    examples: "App development · New service · MVP build · UI/UX · Launch support",
    icon: "product",
    cta: "Talk about a product",
  },
  {
    id: "web",
    title: "Web System & App Development",
    short: "Custom systems & apps",
    body: "Business systems, web applications, dashboards, CRM, booking, e-commerce, and mobile apps.",
    examples: "Business system · Admin dashboard · CRM · Booking · EC · Mobile app",
    icon: "web",
    cta: "Talk about a system",
  },
  {
    id: "ai-ready-web",
    title: "AI-Ready Website Development",
    short: "A website AI & Google can read",
    body: "Standalone website development built so customers, Google, and AI can understand you. SmartPost AI optional; required only for the free starter site campaign.",
    examples: "AI-ready website · Found on Google · Inquiry conversion · Free starter campaign",
    icon: "web",
    cta: "Build an AI-ready website",
  },
  {
    id: "smartpost",
    title: "SmartPost AI",
    short: "AI marketing & post generation",
    body: "SNS post generation, AI marketing support, Facebook content creation, brand-based content at scale.",
    examples: "SNS posts · AI marketing · Facebook content · Brand voice",
    icon: "smartpost",
    cta: "Try SmartPost",
  },
  {
    id: "talent",
    title: "TalentCloud",
    short: "Hiring & talent matching",
    body: "Job posting, recruitment support, talent matching, and candidate introduction.",
    examples: "Job posting · Recruitment · Talent matching · Candidate intros",
    icon: "talent",
    cta: "Hire with TalentCloud",
  },
  {
    // Phase 2 — not a main consultant category. Surfaces only under
    // "Other / Partnership", never in the primary service selection.
    id: "tiktok",
    title: "TikTok / SNS Promotion",
    short: "Video & SNS promotion",
    body: "TikTok video promotion, SNS promotion, short video production, advertising and marketing support.",
    examples: "TikTok video · SNS promotion · Short video · Ads",
    icon: "tiktok",
    cta: "Promote on TikTok / SNS",
    partnership: true,
  },
];

// The six Phase-1 categories the consultant routes between. TikTok / SNS
// Promotion is intentionally excluded — it lives under Other / Partnership.
export const NX_MAIN_SERVICES = NX_SERVICES.filter((s) => !s.partnership);

// Cycling typewriter phrases for the consultant inputs (collapsed + expanded)
// and mirrored on the /services hero. Phrased as a business owner would speak.
export const NX_TYPED_PROMPTS = [
  "I want to use AI in my business…",
  "I want to build an AI-ready website…",
  "I need a booking system for my customers…",
  "I want to launch a new product in Myanmar…",
  "I want to improve SNS marketing with SmartPost AI…",
  "I want to find Myanmar talent…",
];

// Challenges — feed the recommendation engine
export const NX_CHALLENGES = [
  {
    id: "support",
    label: "Too many customer messages to handle",
    sub: "Inbound volume is growing faster than the team can answer.",
    icon: "💬",
    maps: ["ai-support", "ai-integrate"],
  },
  {
    id: "knowledge",
    label: "Team can't find internal info fast",
    sub: "Information is scattered across drives, chats, and PDFs.",
    icon: "📚",
    maps: ["ai-knowledge"],
  },
  {
    id: "sales",
    label: "Sales outreach doesn't scale",
    sub: "Reps spend their day on follow-ups, not on selling.",
    icon: "📈",
    maps: ["ai-sales", "ai-integrate"],
  },
  {
    id: "integrate",
    label: "Systems don't talk to each other",
    sub: "CRM, booking, payments, dashboards — all manual handoffs.",
    icon: "🔗",
    maps: ["ai-integrate", "web-app"],
  },
  {
    id: "mvp",
    label: "A new product / MVP to launch",
    sub: "From idea to a real product in the market.",
    icon: "🚀",
    maps: ["product-mvp"],
  },
  {
    id: "webapp",
    label: "Need a custom web or mobile app",
    sub: "Internal tools, customer portals, or a full mobile app.",
    icon: "📱",
    maps: ["web-app", "product-mvp"],
  },
];

export const NX_INDUSTRIES = [
  "F&B / Restaurants",
  "Logistics & Trade",
  "Retail / E-commerce",
  "Hospitality / Travel",
  "Financial Services",
  "Healthcare",
  "Education",
  "Manufacturing",
  "Other",
];

export const NX_TEAM_SIZES = ["1–10", "11–50", "51–200", "201–1,000", "1,000+"];
export const NX_BUDGETS = ["Under USD 10k", "USD 10–30k", "USD 30–80k", "USD 80k+", "Not sure yet"];
export const NX_TIMELINES = ["ASAP", "1–3 months", "3–6 months", "Exploring"];

// Stage definitions — all 3 variations share the same 7 stages, presented differently.
export const NX_STAGES = [
  { id: "greet",     label: "Greeting" },
  { id: "context",   label: "About you" },
  { id: "challenge", label: "Challenge" },
  { id: "detail",    label: "Detail" },
  { id: "shape",     label: "Scope" },
  { id: "recommend", label: "Recommendation" },
  { id: "summary",   label: "Briefing" },
  { id: "handoff",   label: "Next steps" },
];

// Default scripted state — a realistic in-progress conversation, used by static views.
export const NX_DEMO_STATE = {
  name: "Mya Win",
  role: "Head of Operations",
  company: "Royal Express",
  industry: "Logistics & Trade",
  teamSize: "51–200",
  challenges: ["support", "integrate"],
  detail:
    "We get hundreds of WhatsApp messages a day — order status, address changes, complaints. Two staff are full-time on inbox. We also can't see who has paid in CRM without checking the payment app.",
  budget: "USD 30–80k",
  timeline: "1–3 months",
  contactEmail: "mya@royalexpress.mm",
};

// Naive recommendation — score services by matching challenges, return top 3.
export const recommendServices = function (challengeIds) {
  const score = {};
  (challengeIds || []).forEach((cid) => {
    const c = NX_CHALLENGES.find((x) => x.id === cid);
    if (!c) return;
    c.maps.forEach((sid, i) => {
      score[sid] = (score[sid] || 0) + (i === 0 ? 2 : 1);
    });
  });
  return NX_SERVICES
    .filter((s) => !s.partnership)
    .map((s) => ({ ...s, score: score[s.id] || 0 }))
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score);
};

// Senior team — surfaces as trust signal
export const NX_TEAM = [
  { name: "Kenji Sato",   role: "Principal · AI", initials: "KS", color: "oklch(60% 0.14 270)" },
  { name: "Hnin Su",      role: "Lead PM",        initials: "HS", color: "oklch(64% 0.16 35)"  },
  { name: "Aung Min",     role: "Eng Lead",       initials: "AM", color: "oklch(58% 0.10 158)" },
];

// Mock scripted assistant utterances — used by chat variations
export const NX_SCRIPT = {
  greet: [
    "Hey — I'm Next AI, the consultation assistant for Next Innovations.",
    "I'll ask a few quick questions and pull together a recommendation in about 2 minutes. No sign-up needed.",
    "Want to start with what you're working on, or with a bit about yourself?",
  ],
  contextOk: [
    "Got it — thanks {name}. Knowing the size and shape of {company} helps me tune the recommendation.",
  ],
  challengeAck: [
    "Okay — let's go deeper on {challengeOne}. Most teams hitting this also have a follow-on with {challengeTwo}.",
  ],
  detailAck: [
    "That's a really common pattern in {industry}. I think we can be quite concrete about this.",
  ],
  shapeAck: [
    "Noted — {budget}, {timeline}. That's a tight, doable shape.",
  ],
  recommend: [
    "Here's what I'd suggest. Top of the list is the closest fit; the others are supporting pieces we usually deliver alongside.",
  ],
  summary: [
    "Pulling everything together into a briefing now — your team will get this exact summary so we don't waste your time on the call.",
  ],
};
